import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { formatBrlToNumber } from "../../helpers/formartBrlToNumber";
import {
    getProjectedInvoiceTotalOnAdd,
    isValidInvoiceAmount,
    MIN_INVOICE_AMOUNT_MESSAGE,
} from "../../helpers/invoice/invoiceValidation";
import { setMaskMoney } from "../../helpers/maskMoney";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { api } from "../../services/api";
import ButtonGeneric from "../ButtonGeneric";
import { Container, ContentInput } from "./styles";

const schemaForm = yup
    .object({
        name: yup.string().required("Campo obrigatório"),
        description: yup.string().required("Campo obrigatório"),
        value: yup.string().required("Campo obrigatório"),
        installments: yup.number().required("Campo obrigatório"),
    })
    .required();

type SchemaFormProps = yup.InferType<typeof schemaForm>;

interface Props {
    visible: boolean;
    setVisible: (data: boolean) => void;
    idBilling: number;
    getFinancial: () => void;
    setOpenBillingDetailsModal: (data: boolean) => void;
    currentProductsTotal?: number;
}

function ModalMonthlyBilling({
    visible,
    setVisible,
    idBilling,
    getFinancial,
    setOpenBillingDetailsModal,
    currentProductsTotal = 0,
}: Props) {
    const [isLoadingButton, setIsLoadingButton] = useState(false);

    const { control, handleSubmit, setValue, reset } = useForm<SchemaFormProps>(
        {
            resolver: yupResolver(schemaForm),
            defaultValues: {
                name: "",
                description: "",
                value: "",
                installments: 0,
            },
        }
    );

    const handleClearDatas = () => {
        reset();
        setVisible(false);
    };

    const handleAddMonthlyBilling = async (data: SchemaFormProps) => {
        const amount = formatBrlToNumber(data.value);
        const projectedTotal = getProjectedInvoiceTotalOnAdd(
            currentProductsTotal,
            amount
        );

        if (!isValidInvoiceAmount(projectedTotal)) {
            defaultToast("warning", MIN_INVOICE_AMOUNT_MESSAGE);
            return;
        }

        try {
            setIsLoadingButton(true);
            const responseAddMonthlyBilling = await api.post(
                `monthly-billing/addBilling/${idBilling}`,
                {
                    name: data.name,
                    description: data.description,
                    amount,
                    installments: data.installments,
                }
            );

            if (responseAddMonthlyBilling.status) {
                defaultToast("success", "Faturamento adicionado com sucesso");
                handleClearDatas();
                getFinancial();
                setOpenBillingDetailsModal(false);
            }
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível adicionar o item na fatura."
                )
            );
        } finally {
            setIsLoadingButton(false);
        }
    };

    return (
        <Modal open={visible} onClose={() => setVisible(false)}>
            <Container onSubmit={handleSubmit(handleAddMonthlyBilling)}>
                <h1>Faturamento Mensal</h1>

                <Controller
                    name="name"
                    control={control}
                    render={({
                        field: { onChange },
                        fieldState: { error },
                    }) => {
                        return (
                            <ContentInput>
                                <TextField
                                    onChange={onChange}
                                    fullWidth
                                    size="small"
                                    label="Nome"
                                    error={Boolean(error)}
                                    helperText={error && error.message}
                                />
                            </ContentInput>
                        );
                    }}
                />

                <Controller
                    name="description"
                    control={control}
                    render={({
                        field: { onChange },
                        fieldState: { error },
                    }) => {
                        return (
                            <ContentInput>
                                <TextField
                                    onChange={onChange}
                                    fullWidth
                                    size="small"
                                    label="Descrição"
                                    error={Boolean(error)}
                                    helperText={error && error.message}
                                />
                            </ContentInput>
                        );
                    }}
                />

                <Controller
                    name="value"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => {
                        return (
                            <ContentInput>
                                <TextField
                                    onChange={(event) =>
                                        setValue(
                                            "value",
                                            String(
                                                setMaskMoney(event.target.value)
                                            )
                                        )
                                    }
                                    fullWidth
                                    size="small"
                                    label="Valor"
                                    value={value || ""}
                                    error={Boolean(error)}
                                    helperText={
                                        error?.message ||
                                        "O total da fatura (itens atuais + este valor) deve ser no mínimo R$ 10,00."
                                    }
                                />
                            </ContentInput>
                        );
                    }}
                />

                <Controller
                    name="installments"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => {
                        return (
                            <ContentInput>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="installments-select-label">
                                        Parcelas
                                    </InputLabel>
                                    <Select
                                        labelId="installments-select-label"
                                        id="installments-select"
                                        value={value}
                                        label="Parcelas"
                                        onChange={(event) =>
                                            setValue(
                                                "installments",
                                                Number(event.target.value)
                                            )
                                        }
                                    >
                                        <MenuItem value={0}>
                                            Sem parcela
                                        </MenuItem>
                                        {Array.from(Array(12).keys()).map(
                                            (installments) => {
                                                return (
                                                    <MenuItem
                                                        value={installments + 1}
                                                        key={installments}
                                                    >
                                                        {installments + 1}
                                                    </MenuItem>
                                                );
                                            }
                                        )}
                                    </Select>
                                    {error && (
                                        <FormHelperText error={Boolean(error)}>
                                            {error.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </ContentInput>
                        );
                    }}
                />

                <div className="buttonsContainer">
                    <ButtonGeneric
                        onclick={() => setVisible(false)}
                        buttonColor="var(--cancel)"
                        text="CANCELAR"
                        className="cancelButton"
                    />
                    <LoadingButton
                        title="Salvar"
                        loading={isLoadingButton}
                        type="submit"
                        variant="contained"
                    >
                        Salvar
                    </LoadingButton>
                </div>
            </Container>
        </Modal>
    );
}
export default ModalMonthlyBilling;
